/**
 * Recherche manuelle d'une facture ou d'une famille.
 *
 * ## Pourquoi ce module existe
 *
 * Le premier essai de l'écran a montré un défaut de conception, pas seulement
 * un bogue : quand les suggestions ne trouvaient rien, **le seul geste possible
 * était « hors périmètre »**. Un paiement légitime dont la référence n'avait
 * pas été comprise n'avait donc aucune issue correcte.
 *
 * Le cas réel : `NDSC-2601466`, 394,00 €, pour la facture `CDV-NDSC-2601466`.
 * La référence est complète et sans ambiguïté — seul le préfixe `CDV-` manque.
 * `matching.ts` sait maintenant la lire, mais cela ne suffirait pas : il y aura
 * toujours une communication qu'aucune règle ne rattrape, et l'écran doit
 * permettre de chercher à la main. Une automatisation sans porte de sortie
 * manuelle est une automatisation qu'on ne peut pas utiliser.
 *
 * ## Ce que la recherche fait
 *
 * Elle rend des `Suggestion`, la même forme que les pistes automatiques, pour
 * que l'écran affiche et applique un résultat de recherche exactement comme une
 * suggestion. Deux entrées :
 *
 * - un **numéro de facture**, complet ou partiel : `2601466`, `NDSC-2601466`,
 *   `CDV-NDSC-2601466` mènent tous à la même facture ;
 * - un **nom**, cherché chez les parents et chez les enfants.
 *
 * Contrairement aux suggestions automatiques, la recherche manuelle montre
 * aussi les familles **sans facture ouverte** : savoir qu'une famille existe et
 * n'a rien à payer est une réponse utile.
 */

import { db } from './db';
import { phraseDeFactureTrouvee } from '../factures/types';
import type { FamilyHit, OpenInvoice, Suggestion, SuggestionSource } from './suggest';
import { nameTokens } from './suggest';

/** Au-delà, la recherche est trop vague pour rendre quelque chose d'utile. */
const MIN_CARACTERES = 3;
const MAX_FAMILLES = 12;

interface LigneUser { id: string; nom: string | null; prenom: string | null }

function etiquette(nom?: string | null, prenom?: string | null): string {
  return [nom, prenom].filter(Boolean).join(' ').trim() || 'Compte sans nom';
}

/**
 * Les suites de chiffres exploitables comme fragment de numéro de facture.
 *
 * Quatre chiffres au minimum : en dessous, `26` ou `466` remonteraient des
 * centaines de factures sans rien désigner.
 */
export function fragmentsNumeriques(texte: string): string[] {
  return [...new Set((texte.match(/\d{4,}/g) ?? []).slice(0, 3))];
}

/**
 * Cherche factures et familles, et rend le tout sous forme de suggestions.
 *
 * Les factures trouvées par leur numéro sont volontairement conservées même
 * quand leur solde est nul : si l'administration a tapé ce numéro, lui répondre
 * « rien trouvé » alors que la facture existe et est déjà réglée serait une
 * réponse fausse. C'est précisément l'information qu'elle cherche.
 */
export async function searchManually(
  texte: string,
  paiementCents: number,
): Promise<Suggestion[]> {
  const requete = texte.trim();
  if (requete.length < MIN_CARACTERES) return [];

  const [parNumero, parNom] = await Promise.all([
    chercherFacturesParNumero(requete),
    chercherFamillesParNom(requete),
  ]);

  // Regrouper par famille : une facture trouvée par son numéro et la famille
  // trouvée par son nom peuvent être la même.
  const parFamille = new Map<string, { label: string; sources: Set<SuggestionSource>; reasons: string[] }>();

  const inscrire = (userId: string, label: string, source: SuggestionSource, raison: string) => {
    const e = parFamille.get(userId) ?? { label, sources: new Set<SuggestionSource>(), reasons: [] };
    e.sources.add(source);
    if (!e.reasons.includes(raison)) e.reasons.push(raison);
    if (e.label === 'Compte sans nom' && label !== 'Compte sans nom') e.label = label;
    parFamille.set(userId, e);
  };

  for (const f of parNumero) {
    inscrire(f.userId, f.label, 'invoice_number', phraseDeFactureTrouvee(f));
  }
  for (const h of parNom) {
    inscrire(h.userId, h.label, 'family_search', `nom cherché trouvé : ${h.matched ?? h.label}`);
  }

  if (parFamille.size === 0) return [];

  const ouvertes = await facturesOuvertes([...parFamille.keys()]);

  const suggestions: Suggestion[] = [];
  for (const [userId, e] of parFamille) {
    const factures = ouvertes.get(userId) ?? [];
    const exacte = factures.find((f) => f.outstandingCents === paiementCents) ?? null;
    const total = factures.reduce((s, f) => s + f.outstandingCents, 0);
    const totalExact = factures.length > 1 && total === paiementCents;

    // La facture explicitement cherchée passe en tête de la répartition
    // proposée : c'est celle que l'administration a désignée.
    const designees = new Set(parNumero.filter((f) => f.userId === userId).map((f) => f.invoiceId));
    const ordonnees = [...factures].sort((a, b) => {
      const da = designees.has(a.invoiceId) ? 0 : 1;
      const db_ = designees.has(b.invoiceId) ? 0 : 1;
      return da - db_ || (a.dueDate ?? '9999').localeCompare(b.dueDate ?? '9999');
    });

    const split: { invoice: OpenInvoice; amountCents: number }[] = [];
    let reste = paiementCents;
    for (const f of ordonnees) {
      if (reste <= 0) break;
      const part = Math.min(reste, f.outstandingCents);
      split.push({ invoice: f, amountCents: part });
      reste -= part;
    }

    const reasons = [...e.reasons];
    if (exacte) reasons.push(`le montant correspond exactement à la facture ${exacte.invoiceNumber}`);
    if (factures.length === 0) reasons.push('aucune facture ouverte pour cette famille');

    suggestions.push({
      userId,
      label: e.label,
      sources: [...e.sources],
      // Une facture désignée par son numéro passe avant une famille homonyme.
      score: e.sources.has('invoice_number') ? 100 : 50,
      reasons,
      openInvoices: ordonnees,
      exactInvoice: exacte,
      exactOnTotal: totalExact,
      proposedSplit: split,
    });
  }

  suggestions.sort((a, b) => b.score - a.score || a.label.localeCompare(b.label));
  return suggestions.slice(0, MAX_FAMILLES);
}

/**
 * Factures dont le numéro contient le fragment cherché.
 *
 * La recherche porte sur le numéro complet ET sur les suites de chiffres
 * extraites du texte, ce qui rend équivalents `CDV-NDSC-2601466`,
 * `NDSC-2601466` et `2601466`.
 */
async function chercherFacturesParNumero(requete: string): Promise<
  {
    invoiceId: string; invoiceNumber: string; userId: string; label: string;
    outstandingCents: number; settlement: string;
  }[]
> {
  const motifs = [requete, ...fragmentsNumeriques(requete)]
    .map((m) => m.trim())
    .filter((m) => m.length >= MIN_CARACTERES)
    .map((m) => `invoice_number.ilike.*${m}*`);
  if (motifs.length === 0) return [];

  /*
    Les factures ANNULÉES sont cherchées comme les autres, et c'est une
    correction du 18 septembre 2026.

    Elles étaient écartées par un `.neq('settlement', 'cancelled')`. Chercher
    `CDV-WBK-2600675`, dont la facture existe et est annulée, rendait donc
    « Rien trouvé pour cette recherche » : la famille restait introuvable, donc
    sans suggestion, donc sans destination possible. Le seul geste offert était
    « hors périmètre » — et il est FAUX : l'argent est bien arrivé et il
    appartient à la famille.

    C'était d'autant plus trompeur que la file promet le contraire, dans ses
    propres mots : « la recherche manuelle ci-dessous montre son solde exact ».
    Elle ne le montrait pas.

    La règle de ce module valait déjà pour les factures réglées : « si
    l'administration a tapé ce numéro, lui répondre "rien trouvé" alors que la
    facture existe serait une réponse fausse ». Une annulation ne la change pas.
    Ce qu'il faut, c'est DIRE son état — ce dont l'appelant se charge.

    Mesuré en production : 85 paiements en file, 4 973,70 €, citent une facture
    d'ici — 21 annulées, 64 déjà soldées.
  */
  const { data, error } = await db
    .from('v_invoice_balances')
    .select('invoice_id, invoice_number, user_id, outstanding_cents, settlement')
    .or([...new Set(motifs)].join(','))
    .limit(20);
  if (error) throw error;

  const lignes = (data ?? []) as unknown as {
    invoice_id: string; invoice_number: string; user_id: string;
    outstanding_cents: number; settlement: string;
  }[];
  if (lignes.length === 0) return [];

  const noms = await nomsDeFamille([...new Set(lignes.map((l) => l.user_id))]);
  return lignes.map((l) => ({
    invoiceId: l.invoice_id,
    invoiceNumber: l.invoice_number,
    userId: l.user_id,
    label: noms.get(l.user_id) ?? 'Compte sans nom',
    outstandingCents: l.outstanding_cents,
    settlement: l.settlement,
  }));
}

/** Familles dont le nom du parent, ou celui d'un enfant, contient le texte. */
async function chercherFamillesParNom(requete: string): Promise<FamilyHit[]> {
  const mots = [...nameTokens(requete)].slice(0, 4);
  if (mots.length === 0) return [];
  const ou = (champs: string[]) =>
    mots.flatMap((m) => champs.map((c) => `${c}.ilike.*${m}*`)).join(',');

  const [parents, enfants] = await Promise.all([
    db.from('users').select('id, nom, prenom').or(ou(['nom', 'prenom'])).limit(30),
    db.from('kids').select('nom, prenom, user_id').or(ou(['nom', 'prenom'])).limit(30),
  ]);
  if (parents.error) throw parents.error;
  if (enfants.error) throw enfants.error;

  const hits = new Map<string, FamilyHit>();
  for (const u of (parents.data ?? []) as LigneUser[]) {
    hits.set(u.id, {
      userId: u.id,
      label: etiquette(u.nom, u.prenom),
      source: 'family_search',
      matched: etiquette(u.nom, u.prenom),
    });
  }

  const lignesEnfants = (enfants.data ?? []) as unknown as {
    nom: string; prenom: string; user_id: string;
  }[];
  const manquants = [...new Set(lignesEnfants.map((k) => k.user_id))].filter((id) => !hits.has(id));
  const noms = await nomsDeFamille(manquants);
  for (const k of lignesEnfants) {
    if (hits.has(k.user_id)) continue;
    hits.set(k.user_id, {
      userId: k.user_id,
      label: noms.get(k.user_id) ?? 'Compte sans nom',
      source: 'family_search',
      // Dire QUEL enfant a répondu : c'est ce qui permet de vérifier que la
      // famille proposée est la bonne.
      matched: `${k.prenom} ${k.nom} (enfant)`,
    });
  }
  return [...hits.values()];
}

async function nomsDeFamille(userIds: string[]): Promise<Map<string, string>> {
  const noms = new Map<string, string>();
  if (userIds.length === 0) return noms;
  const { data, error } = await db.from('users').select('id, nom, prenom').in('id', userIds);
  if (error) throw error;
  for (const u of (data ?? []) as LigneUser[]) noms.set(u.id, etiquette(u.nom, u.prenom));
  return noms;
}

async function facturesOuvertes(userIds: string[]): Promise<Map<string, OpenInvoice[]>> {
  const parFamille = new Map<string, OpenInvoice[]>(userIds.map((id) => [id, []]));
  if (userIds.length === 0) return parFamille;

  const { data, error } = await db
    .from('v_invoice_balances')
    .select('invoice_id, invoice_number, user_id, outstanding_cents, due_date, settlement, pdf_url')
    .in('user_id', userIds)
    .gt('outstanding_cents', 0)
    .neq('settlement', 'cancelled')
    .order('due_date', { ascending: true });
  if (error) throw error;

  for (const l of (data ?? []) as unknown as {
    invoice_id: string; invoice_number: string; user_id: string;
    outstanding_cents: number; due_date: string | null; pdf_url: string | null;
  }[]) {
    parFamille.get(l.user_id)?.push({
      invoiceId: l.invoice_id,
      invoiceNumber: l.invoice_number,
      outstandingCents: l.outstanding_cents,
      dueDate: l.due_date,
      pdfUrl: l.pdf_url,
    });
  }
  return parFamille;
}
